import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Card,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Palette as PaletteIcon,
} from "@mui/icons-material";

import EditProfile from "app/Pages/Settings/sections/EditProfile";
import NotificationPreferences from "./sections/NotificationPreferences";
import HostRegister from "./sections/HostRegister";
import ThemeToggle from "app/Components/ThemeToggle";

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      sx={{ flex: 1, p: { xs: 2, md: 3 } }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    "aria-controls": `settings-tabpanel-${index}`,
  };
}

const VerticalTabs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  
  const tabs = [
    {
      label: "Profile",
      icon: <PersonIcon />,
      component: <EditProfile />,
      slug: "profile",
    },
    {
      label: "Notifications",
      icon: <NotificationsIcon />,
      component: <NotificationPreferences />,
      slug: "notifications",
    },
    {
      label: "Appearance",
      icon: <PaletteIcon />,
      component: <ThemeToggle />,
      slug: "appearance",
    },
    {
      label: "Host Registration",
      icon: <BusinessIcon />,
      component: <HostRegister />,
      slug: "host-registration",
    },
  ];

  // Get current tab index from URL
  const getTabIndex = () => {
    if (!tab) return 0;
    const index = tabs.findIndex(t => t.slug === tab);
    return index >= 0 ? index : 0;
  };

  const [value, setValue] = React.useState(getTabIndex());

  // Update tab when URL changes
  useEffect(() => {
    setValue(getTabIndex());
  }, [tab]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabs[newValue];
    if (selectedTab) {
      navigate(`/settings/${selectedTab.slug}`);
    }
  };

  if (isMobile) {
    return (
      <Box>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            variant="fullWidth"
            aria-label="Settings tabs"
            sx={{
              "& .MuiTab-root": {
                minHeight: 56,
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "text.primary",
              },
              "& .Mui-selected": {
                color: "primary.main",
                fontWeight: 600,
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                label={tab.label}
                {...a11yProps(index)}
                sx={{
                  flexDirection: "column",
                  gap: 0.5,
                  "& .MuiSvgIcon-root": {
                    fontSize: "1.25rem",
                  },
                }}
              />
            ))}
          </Tabs>
        </Paper>

        {tabs.map((tab, index) => (
          <TabPanel key={index} value={value} index={index}>
            {tab.slug === "host-registration" ? (
              <Paper
                elevation={2}
                sx={{
                  borderRadius: 2,
                  p: 3,
                  backgroundColor: "background.paper",
                }}
              >
                {tab.component}
              </Paper>
            ) : (
              tab.component
            )}
          </TabPanel>
        ))}
      </Box>
    );
  }

  return (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        minHeight: 600,
      }}
    >
      <Box
        sx={{
          width: 280,
          backgroundColor: "#f5f5f5",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <Tabs
          orientation="vertical"
          value={value}
          onChange={handleChange}
          aria-label="Settings tabs"
          sx={{
            "& .MuiTab-root": {
              alignItems: "flex-start",
              textAlign: "left",
              minHeight: 64,
              px: 3,
              py: 2,
              fontSize: "0.95rem",
              fontWeight: 600,
              justifyContent: "flex-start",
              color: "#333333",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "#f0f0f0",
              },
            },
            "& .Mui-selected": {
              backgroundColor: "#1976d2 !important",
              color: "#ffffff !important",
              "&:hover": {
                backgroundColor: "#1565c0 !important",
              },
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              label={tab.label}
              {...a11yProps(index)}
              sx={{
                flexDirection: "row",
                gap: 2,
                "& .MuiSvgIcon-root": {
                  fontSize: "1.5rem",
                },
                "&.Mui-selected .MuiSvgIcon-root": {
                  color: "#ffffff",
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {tabs.map((tab, index) => (
        <TabPanel key={index} value={value} index={index}>
          {tab.component}
        </TabPanel>
      ))}
    </Card>
  );
};

export default VerticalTabs;
